import React from 'react';
import {
  Box,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  FormControlLabel,
  Checkbox,
  Divider,
} from '@mui/material';
import { BRAND_COLORS, SEMANTIC_COLORS, alpha } from '../../styles/theme';
import { useTranslation } from '../../i18n';
import type { Customer, FlightType, OrgMember, Payload, Pilot, Project } from '../../types/api';
import type { FileInfo } from './FileUploader.styled';
import { UploadButton } from './FileUploader.styled';

interface UploadMetadataDialogProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  currentPath: string;
  selectedFiles: FileInfo[];
  isXerAdmin: boolean;
  loadingOptions: boolean;
  // Option lists
  flightTypes: FlightType[];
  payloads: Payload[];
  pilots: Pilot[];
  customers: Customer[];
  projects: Project[];
  airspaceObservers: OrgMember[];
  payloadOperators: OrgMember[];
  // Field values + setters
  flightType: string;
  setFlightType: React.Dispatch<React.SetStateAction<string>>;
  payload: number | '';
  setPayload: React.Dispatch<React.SetStateAction<number | ''>>;
  pilot: string | '';
  setPilot: React.Dispatch<React.SetStateAction<string | ''>>;
  customer: string | '';
  setCustomer: React.Dispatch<React.SetStateAction<string | ''>>;
  project: string | '';
  setProject: React.Dispatch<React.SetStateAction<string | ''>>;
  airspaceObserver: string | '';
  setAirspaceObserver: React.Dispatch<React.SetStateAction<string | ''>>;
  payloadOperator: string | '';
  setPayloadOperator: React.Dispatch<React.SetStateAction<string | ''>>;
  isTestMode: boolean;
  setIsTestMode: React.Dispatch<React.SetStateAction<boolean>>;
  // Add-option triggers
  onAddPayload: () => void;
  onAddCustomer: () => void;
  onAddProject: () => void;
  // Submit
  onUpload: () => void;
}

const UploadMetadataDialog: React.FC<UploadMetadataDialogProps> = ({
  open,
  onClose,
  loading,
  currentPath,
  selectedFiles,
  isXerAdmin,
  loadingOptions,
  flightTypes,
  payloads,
  pilots,
  customers,
  projects,
  airspaceObservers,
  payloadOperators,
  flightType,
  setFlightType,
  payload,
  setPayload,
  pilot,
  setPilot,
  customer,
  setCustomer,
  project,
  setProject,
  airspaceObserver,
  setAirspaceObserver,
  payloadOperator,
  setPayloadOperator,
  isTestMode,
  setIsTestMode,
  onAddPayload,
  onAddCustomer,
  onAddProject,
  onUpload,
}) => {
  const { t } = useTranslation();
  const hasCsvFiles = selectedFiles.some((f) => f.file.name.toLowerCase().endsWith('.csv'));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('fileManagement.uploadSettings')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          {/* Destination Path Display */}
          <Box>
            <Typography variant="body2" sx={{ mb: 1, color: SEMANTIC_COLORS.text.secondary }}>
              {t('fileManagement.destination')}
            </Typography>
            <Box
              sx={{
                p: 2,
                backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.05),
                border: `1px solid ${alpha(BRAND_COLORS.forestGreen.main, 0.2)}`,
                borderRadius: '8px',
              }}
            >
              <Typography
                variant="body1"
                sx={{ fontFamily: 'monospace', color: BRAND_COLORS.forestGreen.main }}
              >
                /{currentPath || 'root'}
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: SEMANTIC_COLORS.text.secondary, mt: 1 }}>
              {t('fileManagement.destinationHelp')}
            </Typography>
          </Box>

          {/* Only show flight type and payload selection for CSV files */}
          {hasCsvFiles && (
            <>
              <FormControl fullWidth required>
                <InputLabel>{t('fileManagement.flightTypeRequired')}</InputLabel>
                <Select
                  value={flightType}
                  label={t('fileManagement.flightTypeRequired')}
                  onChange={(e) => setFlightType(e.target.value)}
                  required
                  disabled={loadingOptions}
                >
                  <MenuItem value="">
                    <em>
                      {loadingOptions ? t('common.loading') : t('fileManagement.selectFlightType')}
                    </em>
                  </MenuItem>
                  {flightTypes.map((type) => (
                    <MenuItem key={type.name} value={type.name}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth required>
                <InputLabel>{t('fileManagement.payloadRequired')}</InputLabel>
                <Select
                  value={payload}
                  label={t('fileManagement.payloadRequired')}
                  onChange={(e) => setPayload(e.target.value as number)}
                  required
                  disabled={loadingOptions}
                >
                  <MenuItem value="">
                    <em>
                      {loadingOptions ? t('common.loading') : t('fileManagement.selectPayload')}
                    </em>
                  </MenuItem>
                  {payloads.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name} {p.weight ? `(${p.weight}kg)` : ''}
                    </MenuItem>
                  ))}
                  <Divider />
                  <MenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      onAddPayload();
                    }}
                    sx={{ fontStyle: 'italic', fontSize: '0.875rem' }}
                  >
                    + {t('fileManagement.addPayload')}
                  </MenuItem>
                </Select>
              </FormControl>

              {flightType !== 'BI' && flightType !== 'RT' && (
                <FormControl fullWidth required>
                  <InputLabel>{t('fileManagement.pilotRequired')}</InputLabel>
                  <Select
                    value={pilot}
                    label={t('fileManagement.pilotRequired')}
                    onChange={(e) => setPilot(e.target.value as string)}
                    required
                    disabled={loadingOptions}
                  >
                    <MenuItem value="">
                      <em>
                        {loadingOptions ? t('common.loading') : t('fileManagement.selectPilot')}
                      </em>
                    </MenuItem>
                    {pilots.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.full_name || p.email}
                      </MenuItem>
                    ))}
                    <MenuItem value="other">
                      <em>{t('common.other')}</em>
                    </MenuItem>
                  </Select>
                </FormControl>
              )}

              {/* Customer dropdown (optional) */}
              <FormControl fullWidth>
                <InputLabel>{t('fileManagement.customer')}</InputLabel>
                <Select
                  value={customer}
                  label={t('fileManagement.customer')}
                  onChange={(e) => setCustomer(e.target.value as string)}
                  disabled={loadingOptions}
                >
                  <MenuItem value="">
                    <em>
                      {loadingOptions ? t('common.loading') : t('fileManagement.selectCustomer')}
                    </em>
                  </MenuItem>
                  {customers.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                  <Divider />
                  <MenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      onAddCustomer();
                    }}
                    sx={{ fontStyle: 'italic', fontSize: '0.875rem' }}
                  >
                    + {t('fileManagement.addCustomer')}
                  </MenuItem>
                </Select>
              </FormControl>

              {/* Project dropdown (optional) */}
              <FormControl fullWidth>
                <InputLabel>{t('fileManagement.project')}</InputLabel>
                <Select
                  value={project}
                  label={t('fileManagement.project')}
                  onChange={(e) => setProject(e.target.value as string)}
                  disabled={loadingOptions}
                >
                  <MenuItem value="">
                    <em>
                      {loadingOptions ? t('common.loading') : t('fileManagement.selectProject')}
                    </em>
                  </MenuItem>
                  {projects.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
                  <Divider />
                  <MenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      onAddProject();
                    }}
                    sx={{ fontStyle: 'italic', fontSize: '0.875rem' }}
                  >
                    + {t('fileManagement.addProject')}
                  </MenuItem>
                </Select>
              </FormControl>

              {/* Airspace Observer dropdown (optional) */}
              <FormControl fullWidth>
                <InputLabel>{t('fileManagement.airspaceObserver')}</InputLabel>
                <Select
                  value={airspaceObserver}
                  label={t('fileManagement.airspaceObserver')}
                  onChange={(e) => setAirspaceObserver(e.target.value as string)}
                  disabled={loadingOptions}
                >
                  <MenuItem value="">
                    <em>
                      {loadingOptions
                        ? t('common.loading')
                        : t('fileManagement.selectAirspaceObserver')}
                    </em>
                  </MenuItem>
                  {airspaceObservers.map((o) => (
                    <MenuItem key={o.id} value={o.id}>
                      {o.full_name || o.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Payload Operator dropdown (optional) */}
              <FormControl fullWidth>
                <InputLabel>{t('fileManagement.payloadOperator')}</InputLabel>
                <Select
                  value={payloadOperator}
                  label={t('fileManagement.payloadOperator')}
                  onChange={(e) => setPayloadOperator(e.target.value as string)}
                  disabled={loadingOptions}
                >
                  <MenuItem value="">
                    <em>
                      {loadingOptions
                        ? t('common.loading')
                        : t('fileManagement.selectPayloadOperator')}
                    </em>
                  </MenuItem>
                  {payloadOperators.map((o) => (
                    <MenuItem key={o.id} value={o.id}>
                      {o.full_name || o.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box
                sx={{
                  p: 2,
                  backgroundColor: alpha(BRAND_COLORS.yellow.main, 0.1),
                  borderRadius: '8px',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: BRAND_COLORS.yellow.main, fontWeight: 'medium' }}
                >
                  {flightType === 'BI' || flightType === 'RT'
                    ? `ℹ️ ${t('fileManagement.flightTypePayloadRequired')}`
                    : `ℹ️ ${t('fileManagement.flightTypePayloadPilotRequired')}`}
                </Typography>
              </Box>
            </>
          )}

          {/* Show info for non-CSV files */}
          {selectedFiles.length > 0 && !hasCsvFiles && (
            <Box
              sx={{
                p: 2,
                backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.1),
                borderRadius: '8px',
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: BRAND_COLORS.forestGreen.main, fontWeight: 'medium' }}
              >
                ✓ {t('fileManagement.documentFilesInfo')}
              </Typography>
            </Box>
          )}

          {/* Test mode checkbox - only visible for xer_admins */}
          {isXerAdmin && hasCsvFiles && (
            <Box
              sx={{
                p: 2,
                backgroundColor: alpha(BRAND_COLORS.yellow.main, 0.1),
                borderRadius: '8px',
                border: `1px solid ${alpha(BRAND_COLORS.yellow.main, 0.3)}`,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isTestMode}
                    onChange={(e) => setIsTestMode(e.target.checked)}
                    sx={{
                      color: BRAND_COLORS.yellow.main,
                      '&.Mui-checked': {
                        color: BRAND_COLORS.yellow.main,
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {t('fileManagement.testModeLabel')}
                  </Typography>
                }
              />
            </Box>
          )}

          <Box>
            <Typography variant="body2" sx={{ mb: 1, color: SEMANTIC_COLORS.text.secondary }}>
              {t('fileManagement.filesToUpload')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedFiles.map((fileInfo, index) => (
                <Chip
                  key={index}
                  label={fileInfo.file.name}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: BRAND_COLORS.forestGreen.main,
                    color: BRAND_COLORS.forestGreen.main,
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <UploadButton
          onClick={onUpload}
          disabled={
            loading ||
            (!isTestMode &&
              hasCsvFiles &&
              (!flightType || !payload || (!pilot && flightType !== 'BI' && flightType !== 'RT')))
          }
        >
          {t('fileManagement.uploadCount', { count: selectedFiles.length })}
        </UploadButton>
      </DialogActions>
    </Dialog>
  );
};

export default UploadMetadataDialog;
